import { defineStore } from 'pinia'
import axios from 'axios'
import http from '@/http-common'
import { devLog, devError } from '@/services/logger'

const url = (import.meta as any).env.VITE_API_URL

export const authStore = defineStore('auth', {
  state: () => {
    return {
      token: localStorage.getItem('token'),
      refresh: localStorage.getItem('refresh'),
      logged_at: localStorage.getItem('logged_at'),

      empSlug: localStorage.getItem('empSlug'),
      cmpU: localStorage.getItem('cmpU'),
      cpmUName: localStorage.getItem('cmpUName'),
      cmpUadmin: localStorage.getItem('cmpUadmin'),
      cmpUrole: localStorage.getItem('cmpUrole'),
      empEnabled: localStorage.getItem('empEnabled'),
      cmpId: localStorage.getItem('cmpId'),

      addU: localStorage.getItem('addUsers'),

      emailValidated: localStorage.getItem('emailValidated'),

      usrSlug: localStorage.getItem('usrSlug'),
      online: localStorage.getItem('online'),

      empUUID: localStorage.getItem('empUUID'),
      needsCompanySelection: false,
      availableCompanies: JSON.parse(localStorage.getItem('availableCompanies') || '[]'),
    }
  },
  actions: {
    updateFunc(data: any) {
      // Handle new multi-tenant structure from /me
      const companies = data.companies || []
      const activeCompany = data.active_company || data.current_company || data.activeCompany || data.company || {}

      const resolvedCompanyId = activeCompany.id || data.cmpId || this.cmpId
      const activeCompanyFromList = companies.find((c: any) => String(c?.id) === String(resolvedCompanyId))

      // Some backends return the company nested, e.g. { company: { slug, uuid, id } }
      const activeCompanyNested = (activeCompany as any)?.company || null

      this.empSlug =
        (activeCompany as any)?.empSlug ||
        (activeCompany as any)?.slug ||
        activeCompanyNested?.slug ||
        (activeCompanyFromList as any)?.slug ||
        data.empSlug ||
        data.emp_slug ||
        null
      this.cmpU = activeCompany.cmpU || data.cmpU || data.id
      this.cpmUName = data.cmpUName || data.fullname
      this.cmpUadmin = activeCompany.cmpUadmin ?? activeCompany.is_admin ?? data.cmpUadmin
      this.cmpUrole =
        activeCompany.cmpUrole ||
        data.cmpUrole ||
        data.role ||
        data.company_user?.role ||
        data.companyUser?.role ||
        data.company_user_role ||
        data.companyUserRole ||
        null
      this.empEnabled = activeCompany.empEnabled ?? activeCompanyNested?.empEnabled ?? data.empEnabled ?? true
      this.emailValidated = data.emailValidated
      this.cmpId = resolvedCompanyId ? String(resolvedCompanyId) : null
      this.usrSlug = data.usrSlug
      this.addU = data.addU
      this.online = data.online
      this.empUUID =
        (activeCompany as any)?.uuid ||
        activeCompanyNested?.uuid ||
        (activeCompanyFromList as any)?.uuid ||
        data.empUUID
      this.availableCompanies = companies
      this.needsCompanySelection = !!data.needsCompanySelection || (companies.length > 0 && !this.cmpId)

      if (this.empSlug) {
        localStorage.setItem('empSlug', String(this.empSlug))
      } else {
        localStorage.removeItem('empSlug')
      }
      localStorage.setItem('cmpU', String(this.cmpU || ''))
      localStorage.setItem('cmpUName', this.cpmUName || '')
      localStorage.setItem('cmpUadmin', String(this.cmpUadmin || ''))
      if (this.cmpUrole) {
        localStorage.setItem('cmpUrole', String(this.cmpUrole))
      } else {
        localStorage.removeItem('cmpUrole')
      }
      localStorage.setItem('empEnabled', String(this.empEnabled))
      localStorage.setItem('emailValidated', String(this.emailValidated || ''))

      if (this.cmpId) {
        localStorage.setItem('cmpId', String(this.cmpId))
      } else {
        localStorage.removeItem('cmpId')
      }

      localStorage.setItem('usrSlug', this.usrSlug || '')
      localStorage.setItem('addU', String(this.addU || ''))
      localStorage.setItem('online', String(this.online || ''))
      localStorage.setItem('empUUID', this.empUUID || '')
      localStorage.setItem('availableCompanies', JSON.stringify(this.availableCompanies))
    },
    async switchCompany(companyId: string | number) {
      devLog(`🏢 Switching to company: ${companyId}`)
      this.cmpId = String(companyId)
      localStorage.setItem('cmpId', this.cmpId)

      // Best-effort update of company slug/uuid for immediate UI feedback.
      // Final authoritative values will be refreshed via /me.
      const company = (this.availableCompanies || []).find((c: any) => String(c?.id) === String(companyId))
      if (company?.slug) {
        this.empSlug = String(company.slug)
        localStorage.setItem('empSlug', this.empSlug)
      }
      if (company?.uuid) {
        this.empUUID = String(company.uuid)
        localStorage.setItem('empUUID', this.empUUID)
      }

      // The actual context update (roles, permissions) will happen when we re-fetch /me
      // and reload permissions, which should be handled by the caller (e.g., Navbar or CompanySelection)
      // after clearing the stores.
    },
    updateToken(token: string, refresh: string, logged_at: string) {
      devLog('🔄 Updating tokens in auth store')

      this.token = token
      this.refresh = refresh
      this.logged_at = logged_at

      localStorage.setItem('token', token)
      localStorage.setItem('refresh', refresh)
      localStorage.setItem('logged_at', logged_at)

      // document.cookie = 'token=' + this.token + '; path=/; SameSite=Lax; Secure;' //    HttpOnly;
      document.cookie = 'token=' + this.token + '; path=/; SameSite=Lax;'

      devLog('✅ Tokens updated successfully')
    },
    removeToken() {
      document.cookie = 'csrftoken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax;'
      document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax;'

      this.token = null
      this.refresh = null
      this.logged_at = null

      this.empSlug = null
      this.cmpU = null
      this.cpmUName = null
      this.cmpUadmin = null
      this.cmpUrole = null
      this.empEnabled = null

      this.emailValidated = null
      this.cmpId = null
      this.usrSlug = null
      this.addU = null
      this.online = null
      this.empUUID = null

      localStorage.removeItem('token')
      localStorage.removeItem('refresh')
      localStorage.removeItem('logged_at')

      localStorage.removeItem('empSlug')
      localStorage.removeItem('cmpU')
      localStorage.removeItem('cmpUName')
      localStorage.removeItem('cmpUadmin')
      localStorage.removeItem('cmpUrole')
      localStorage.removeItem('empEnabled')

      localStorage.removeItem('emailValidated')
      localStorage.removeItem('cmpId')
      localStorage.removeItem('usrSlug')
      localStorage.removeItem('addU')
      localStorage.removeItem('online')
      localStorage.removeItem('empUUID')
      localStorage.removeItem('companyPlanSummary')
      localStorage.removeItem('companyPlanSummaryCompanyId')
    },
    async refresh_app_layout() {
      // Uses raw axios intentionally to avoid circular dependency with http-common interceptor.
      // Adds X-Company-ID manually for defense-in-depth.
      try {
        const headers: Record<string, string> = {}
        if (this.cmpId && this.cmpId !== 'null' && this.cmpId !== 'undefined') {
          headers['X-Company-ID'] = String(this.cmpId)
        }
        const response = await axios.post(url + '/token/refresh/', { refresh: this.refresh }, { headers })

        const { access, refresh } = response.data
        this.updateToken(access, refresh, new Date().toISOString())

        return response // Return the response for the http interceptor
      } catch (error) {
        devError(error)
        throw error // Re-throw the error so the interceptor can handle it
      }
    },
    confirmContract() {
      try {
        return http.post('/empresa/confirm-contract/', {})
      } catch (error) {
        devError(error)
      }
    },
    checkMessages() {
      try {
        return http.get('/sse/messages/')
      } catch (error) {
        devError(error)
      }
    },
    changeUserStatus() {
      try {
        const resp = http.post('/usuario/changeUserStatus/', { cmpU: this.cmpU })

        resp.then(() => {
          localStorage.setItem('online', localStorage.getItem('online') === 'true' ? 'false' : 'true')
        })
      } catch (error) {
        devError(error)
      }
    },
  },
})
